import { StandardEvents } from '@shopify/events';

/**
 * Keeps custom product blocks in step with Horizon's variant picker.
 *
 * Horizon dispatches `StandardEvents.productSelect` on every variant change. On a
 * standard product page `variant-picker.js` builds its request URL without a
 * `section_id` (the SECTION_ID_MAP there only matches quick-add, swatch pickers and
 * featured-product), so the response is the *whole* re-rendered product page and
 * `detail.html` is a full Document — not just the product-information section.
 *
 * Any element marked `data-cr-sync="<unique id>"` is therefore swapped for its
 * counterpart in that HTML, wherever it sits on the page. That lets sections outside
 * the buy box (the specification table, the sizes table, the delivery panel) stay
 * server-rendered and variant-accurate without duplicating variant logic in JS and
 * without a second network request.
 *
 * While a change is loading, the add-to-cart button is disabled and the section is
 * marked busy, so a radiator can never be added with the previous configuration's
 * cart properties or add-ons (blocks/cr-cart-details.liquid, blocks/cr-addons.liquid).
 * Horizon re-enables the button for an available variant once the change resolves;
 * this module only restores it itself when Horizon did not. Changed specification
 * values and the price are briefly highlighted afterwards.
 */

let boundToDocument = false;

/** Latest change per section, so a superseded response never re-enables the button. */
const generations = new WeakMap();

const HIGHLIGHT_CLASS = 'cr-value-changed';

/** @param {Element} section */
function addToCartButtons(section) {
  return /** @type {HTMLButtonElement[]} */ (
    Array.from(section.querySelectorAll('product-form-component [ref="addToCartButton"]'))
  );
}

/** @param {Element} section */
function priceText(section) {
  return section.querySelector('product-price')?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
}

/** @returns {Map<string, string>} */
function specValues() {
  const values = new Map();
  document.querySelectorAll('[data-cr-spec-key]').forEach((row) => {
    const key = row.getAttribute('data-cr-spec-key');
    if (key) values.set(key, row.querySelector('dd')?.textContent?.replace(/\s+/g, ' ').trim() ?? '');
  });
  return values;
}

/** @param {Element | null | undefined} element */
function highlight(element) {
  if (!(element instanceof HTMLElement)) return;
  element.classList.remove(HIGHLIGHT_CLASS);
  // Restart the animation when the same value changes twice in a row.
  void element.offsetWidth;
  element.classList.add(HIGHLIGHT_CLASS);
  element.addEventListener('animationend', () => element.classList.remove(HIGHLIGHT_CLASS), { once: true });
}

function syncFromEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  // Product cards and quick-add dialogs render their own variant pickers for *other*
  // products (recommendations, complementary products). They must never rewrite the
  // main product's blocks.
  if (target.closest('product-card, quick-add-dialog')) return;

  const originSection = target.closest('.shopify-section');
  if (!originSection) return;

  const generation = (generations.get(originSection) ?? 0) + 1;
  generations.set(originSection, generation);

  const previousSpecs = specValues();
  const previousPrice = priceText(originSection);

  originSection.setAttribute('aria-busy', 'true');
  originSection.classList.add('cr-is-updating');
  for (const button of addToCartButtons(originSection)) button.disabled = true;

  const settle = (/** @type {{ available: boolean } | null} */ outcome) => {
    if (generations.get(originSection) !== generation) return;
    originSection.removeAttribute('aria-busy');
    originSection.classList.remove('cr-is-updating');
    if (outcome?.available) {
      for (const button of addToCartButtons(originSection)) button.disabled = false;
    }
  };

  event.promise
    ?.then(({ detail }) => {
      const html = detail?.html;
      if (!html) {
        settle({ available: Boolean(detail?.resource && detail.resource.available !== false) });
        return;
      }

      // Guard against a stale or foreign response rewriting the page.
      const pickerProductId = target.closest('variant-picker')?.dataset.productId;
      if (pickerProductId && detail.productId && pickerProductId !== detail.productId) {
        settle(null);
        return;
      }

      document.querySelectorAll('[data-cr-sync]').forEach((current) => {
        const id = current.getAttribute('data-cr-sync');
        if (!id) return;
        const next = html.querySelector(`[data-cr-sync="${CSS.escape(id)}"]`);
        if (!next) return;
        current.replaceWith(document.importNode(next, true));
      });

      settle({ available: Boolean(detail.resource && detail.resource.available !== false) });

      // Horizon's price component updates from the same event; read it on the next frame.
      requestAnimationFrame(() => {
        const nextSpecs = specValues();
        nextSpecs.forEach((value, key) => {
          if (previousSpecs.has(key) && previousSpecs.get(key) !== value) {
            highlight(document.querySelector(`[data-cr-spec-key="${CSS.escape(key)}"] dd`));
          }
        });
        if (previousPrice && priceText(originSection) !== previousPrice) {
          highlight(originSection.querySelector('product-price'));
        }
      });

      // Dispatched on the originating section so that both section-scoped listeners
      // (cr-addons, cr-bespoke-colour-field) and document-level listeners receive it.
      originSection.dispatchEvent(new CustomEvent('cr:product-synced', { bubbles: true }));
    })
    .catch((error) => {
      // An aborted request was superseded by a newer selection, which settles itself.
      if (error?.name === 'AbortError') return;
      settle({ available: true });
    });
}

function bind() {
  if (boundToDocument) return;
  boundToDocument = true;
  document.addEventListener(StandardEvents.productSelect, syncFromEvent);
}

bind();
